import React, { useState, useEffect } from "react";
import { Button, Input, notification } from "antd";
import {
  fetchDownloadProduct,
  processCardPhonePayment,
  fetchProviderTelecom,
} from "../../services/topupService";
import VoucherSection from "../VoucherSection";
import { triggerMiniAppPayment } from "../../services/paymentUtils";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const CardPhone = ({ setLoading, loading, token }) => {
  const [networks, setNetworks] = useState([]);
  const [cardPhoneInfo, setCardPhoneInfo] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailError, setEmailError] = useState("");
  const [voucherValue, setVoucherValue] = useState(0);
  const [voucherCode, setVoucherCode] = useState("");

  const formatVND = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  const validateEmail = (email) => {
    if (!email.trim()) {
      setEmailError("Vui lòng nhập email");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Email không hợp lệ");
      return false;
    }
    setEmailError("");
    return true;
  };

  const subtotal = selectedCard ? selectedCard.productamount * quantity : 0;
  const finalPrice = Math.max(subtotal - voucherValue, 0);

  const fetchCardDataByProvider = async (providerCode) => {
    if (!providerCode) return;
    setLoading(true);
    try {
      const res = await fetchDownloadProduct(providerCode);
      const data = res?.data?.data || [];
      setCardPhoneInfo(data);
      if (data.length > 0) {
        setSelectedCard(data[0]);
      }
    } catch (error) {
      setCardPhoneInfo([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProviderSelect = (net) => {
    setSelectedProvider(net);
    setSelectedCard(null);
    setEmailRecipient("");
    setEmailError("");
    setQuantity(1);
    setVoucherCode("");
    setVoucherValue(0);
    fetchCardDataByProvider(net.providercode);
  };

  const handleCardSelect = (card) => {
    setSelectedCard(card);
    setQuantity(1);
  };

  const handlePayment = async () => {
    if (!selectedCard || !validateEmail(emailRecipient)) return;
    setLoading(true);
    try {
      const paymentData = {
        ProviderCode: selectedProvider?.providercode,
        Amount: finalPrice,
        AmountItem: selectedCard.productamount,
        ProductId: selectedCard.productid,
        Quantity: quantity,
        EmailRecipient: emailRecipient.trim(),
        VoucherCode: voucherCode.trim(),
      };
      paymentData.InfoOrder = JSON.stringify({
        Service: "cardmobile",
        Sponsor: "IPASS",
        ProviderName: selectedProvider?.providername,
        Amount: paymentData.Amount,
        Email: paymentData.EmailRecipient,
        Quantity: paymentData.Quantity,
        VoucherCode: voucherCode.trim() || "",
        VoucherValue: voucherValue || 0,
        AmountItem: selectedCard.productamount,
      });
      const paymentResponse = await processCardPhonePayment(paymentData);
      if (paymentResponse?.data?.data?.ordercode) {
        await triggerMiniAppPayment(paymentResponse.data.data.ordercode, finalPrice);
      } else {
        notification.error({
          message: "Thanh toán thất bại",
          description: paymentResponse?.data?.message || "Không tìm thấy order code",
        });
      }
    } catch (error) {
      notification.error({
        message: "Thanh toán thất bại",
        description: error.response?.data?.err || "Đã xảy ra lỗi khi thanh toán",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      const loadNetworks = async () => {
        setLoading(true);
        try {
          const res = await fetchProviderTelecom();
          const data = (res?.data?.data || res?.data || []).filter((n) => n.status === 1);
          setNetworks(data);
          if (data.length > 0) {
            handleProviderSelect(data[0]);
          }
        } catch (err) {
          setNetworks([]);
        } finally {
          setLoading(false);
        }
      };
      loadNetworks();
    }
  }, [token]);

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
      {networks.length > 0 && (
        <div className="bg-white p-4 rounded-2xl shadow-sm mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Chọn nhà mạng
          </label>
          <div className="grid grid-cols-4 gap-2">
            {networks.map((net) => {
              const isSelected = selectedProvider?.providercode === net.providercode;
              return (
                <Button
                  key={net.providercode}
                  onClick={() => handleProviderSelect(net)}
                  ghost={true}
                  className={`py-3 px-2 rounded-xl flex flex-col items-center gap-2 h-auto ${isSelected
                    ? "!bg-primary-50 !border !border-primary !text-primary shadow-sm"
                    : "!bg-white !border !border-gray-200 !text-gray-500 hover:!border-primary hover:!text-primary"
                    }`}
                >
                  <div className="w-15 h-14 flex items-center justify-center">
                    {net.link ? (
                      <img
                        src={`${BASE_URL}${net.link}`}
                        alt={net.providername}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <span className="text-xs font-bold text-center">{net.providername}</span>
                    )}
                  </div>
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {cardPhoneInfo.length > 0 && (
        <div className="bg-white p-4 rounded-2xl shadow-sm mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Chọn mệnh giá thẻ
          </label>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {cardPhoneInfo.map((card) => {
              const isActive = selectedCard?.productid === card.productid;
              return (
                <Button
                  key={card.productid}
                  onClick={() => handleCardSelect(card)}
                  type={isActive ? "primary" : "default"}
                  className="!rounded-xl !font-medium !h-11 !px-4"
                >
                  {formatVND(card.productamount)}
                </Button>
              );
            })}
          </div>

          {selectedCard && (
            <>
              <div className="border-t border-gray-100 pt-4">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-semibold text-gray-700">
                    Số lượng
                  </label>
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      className="!w-9 !h-9 !min-w-0 !p-0 flex items-center justify-center !text-base font-bold"
                    >
                      -
                    </Button>
                    <span className="font-bold w-6 text-center">{quantity}</span>
                    <Button
                      onClick={() => setQuantity(quantity + 1)}
                      className="!w-9 !h-9 !min-w-0 !p-0 flex items-center justify-center !text-base font-bold"
                    >
                      +
                    </Button>
                  </div>
                </div>

                <VoucherSection
                  total={selectedCard ? selectedCard.productamount * quantity : 0}
                  quantity={quantity}
                  disabled={!selectedCard}
                  resetKey={selectedProvider?.providercode}
                  onChange={({ code, value }) => {
                    setVoucherCode(code);
                    setVoucherValue(value);
                  }}
                />

                <div className="space-y-1.5 text-sm">
                  {voucherValue > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Giảm giá</span>
                      <span className="text-green-600 font-medium">-{formatVND(voucherValue)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-gray-100 pt-1.5">
                    <span className="font-semibold text-gray-800">Thành tiền</span>
                    <span className="font-bold text-primary-600">{formatVND(finalPrice)}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {cardPhoneInfo.length === 0 && selectedProvider && (
        <div className="bg-white p-4 rounded-2xl shadow-sm mb-4">
          <div className="text-center py-6 text-gray-400 text-sm">
            Không có mệnh giá nào
          </div>
        </div>
      )}

      {selectedCard && (
        <div className="bg-white p-4 rounded-2xl shadow-sm mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Email nhận thẻ <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Nhập email nhận thẻ"
            value={emailRecipient}
            onChange={(e) => {
              setEmailRecipient(e.target.value);
              if (emailError) validateEmail(e.target.value);
            }}
            onBlur={() => validateEmail(emailRecipient)}
            status={emailError ? "error" : ""}
            className="rounded-xl"
            size="large"
          />
          {emailError && (
            <p className="text-red-500 text-xs mt-1">{emailError}</p>
          )}
        </div>
      )}



      {selectedCard && (
        <Button
          type="primary"
          loading={loading}
          onClick={handlePayment}
          disabled={!emailRecipient.trim() || !!emailError}
          className="w-full !rounded-xl !shadow-lg !py-4 !h-auto"
        >
          <span className="text-white font-bold text-lg">
            Thanh toán {formatVND(finalPrice)}
          </span>
        </Button>
      )}
    </div>
  );
};

export default CardPhone;